import React from 'react';
import { Box, Typography, TextField, InputAdornment, Alert, Autocomplete } from '@mui/material';
import { Grid2 } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import AirIcon from '@mui/icons-material/Air';
import ExploreIcon from '@mui/icons-material/Explore';
import CloudIcon from '@mui/icons-material/Cloud';

// Wind direction options
const WIND_DIRECTIONS = [
    { value: 'N', label: 'N' },
    { value: 'N/NO', label: 'N/NO' },
    { value: 'NO', label: 'NO' },
    { value: 'O/NO', label: 'O/NO' },
    { value: 'O', label: 'O' },
    { value: 'O/ZO', label: 'O/ZO' },
    { value: 'ZO', label: 'ZO' },
    { value: 'Z/ZO', label: 'Z/ZO' },
    { value: 'Z', label: 'Z' },
    { value: 'Z/ZW', label: 'Z/ZW' },
    { value: 'ZW', label: 'ZW' },
    { value: 'W/ZW', label: 'W/ZW' },
    { value: 'W', label: 'W' },
    { value: 'W/NW', label: 'W/NW' },
    { value: 'NW', label: 'NW' },
    { value: 'N/NW', label: 'N/NW' },
];

// Shared data shape for standalone (non-form) usage (e.g. TaskDetailScreen)
export interface TaskWeatherData {
    temperature: number | string | null;
    wind_speed: number | string | null;
    wind_direction: string | null;
    weather_code: string | null;
}

interface WeatherFieldsProps {
    type: 'api' | 'manual';
    isMobile?: boolean;
    isOnline?: boolean;
    weatherData?: TaskWeatherData | null;
    onWeatherChange?: (data: Partial<TaskWeatherData>) => void;
    weatherErrors?: { temperature?: string; wind_speed?: string; weather_code?: string };
}

// Readonly weather display card
const WeatherCard: React.FC<{
    icon: React.ReactNode;
    iconBg: string;
    label: string;
    value: string | number | null | undefined;
    unit: string;
}> = ({ icon, iconBg, label, value, unit }) => (
    <Box sx={{
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: 1,
        px: 1.5,
        py: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: 'white',
        height: '56px',
    }}>
        <Box sx={{
            bgcolor: iconBg,
            borderRadius: '50%',
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        }}>
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
                fontSize: '0.58rem',
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                lineHeight: 1.2,
            }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.3, mt: 0.15, wordBreak: 'break-word' }}>
                {value !== null && value !== undefined && value !== '' ? value : '—'}
                {unit && value !== null && value !== undefined && value !== '' && (
                    <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 400, ml: 0.4 }}>
                        {unit}
                    </Typography>
                )}
            </Typography>
        </Box>
    </Box>
);



export const WeatherFieldSection: React.FC<WeatherFieldsProps> = ({
    type,
    weatherData,
    onWeatherChange,
    weatherErrors,
    isOnline = true,
}) => {
    const theme = useTheme();
    const isReadonly = type === 'api';

    // Resolve display values for API section
    const apiTemp = weatherData?.temperature ?? null;
    const apiWind = weatherData?.wind_speed ?? null;
    const apiDir = weatherData?.wind_direction ?? null;
    const apiCode = weatherData?.weather_code ?? null;

    if (isReadonly) {
        return (
            <Box sx={{ mb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{
                        fontFamily: 'Nunito Sans Regular700',
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                    }}>
                        Weersomstandigheden (API)
                    </Typography>
                    {/* <Chip
                        label="AUTOMATISCH"

                        sx={{
                            bgcolor: '#e3f2fd',
                            color: '#1565c0',
                            fontWeight: 700,
                            fontSize: '0.58rem',
                            letterSpacing: 0.5,
                            height: 20,
                        }}
                    /> */}
                </Box>
                <Grid2 container spacing={1.5}>
                    <Grid2 size={{ xs: 6, sm: 6 }}>
                        <WeatherCard
                            icon={<DeviceThermostatIcon sx={{ color: '#f57c00', fontSize: 17 }} />}
                            iconBg="#fff3e0"
                            label="TEMP"
                            value={apiTemp}
                            unit="°C"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 6 }}>
                        <WeatherCard
                            icon={<AirIcon sx={{ color: '#1976d2', fontSize: 17 }} />}
                            iconBg="#e3f2fd"
                            label="WIND"
                            value={apiWind}
                            unit="km/u"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 6 }}>
                        <WeatherCard
                            icon={<ExploreIcon sx={{ color: '#7b1fa2', fontSize: 17 }} />}
                            iconBg="#f3e5f5"
                            label="RICHTING"
                            value={apiDir}
                            unit=""
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 6 }}>
                        <WeatherCard
                            icon={<CloudIcon sx={{ color: '#455a64', fontSize: 17 }} />}
                            iconBg="#eceff1"
                            label="LUCHT"
                            value={apiCode}
                            unit=""
                        />
                    </Grid2>
                </Grid2>
                {!isOnline && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            Weersgegevens kunnen niet worden opgehaald vanwege een netwerkprobleem.
                        </Typography>
                    </Alert>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 0, mt: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{
                    fontFamily: 'Nunito Sans Regular700',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                }}>
                    Weersomstandigheden (Handmatig)
                </Typography>
                {/* <Chip
                    label="HANDMATIG"

                    sx={{
                        bgcolor: '#fff3e0',
                        color: '#e65100',
                        fontWeight: 700,
                        fontSize: '0.58rem',
                        letterSpacing: 0.5,
                        height: 20,
                    }}
                /> */}
            </Box>
            <Grid2 container spacing={1.5}>
                {/* Temperature */}
                <Grid2 size={{ xs: 6 }}>
                    <TextField
                        fullWidth
                        value={weatherData?.temperature === null || weatherData?.temperature === undefined ? '' : weatherData.temperature}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                                onWeatherChange?.({ temperature: value === '' ? null : value });
                            }
                        }}
                        onBlur={() => {
                            const value = weatherData?.temperature;
                            if (value === null || value === undefined || value === '' || value === '-') {
                                onWeatherChange?.({ temperature: null });
                            } else {
                                const numValue = Number(value);
                                if (!isNaN(numValue)) onWeatherChange?.({ temperature: numValue });
                            }
                        }}
                        variant="outlined"
                        placeholder="0.0"
                        type="text"
                        inputMode="decimal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>°C</Typography>
                                </InputAdornment>
                            ),
                        }}
                        error={!!weatherErrors?.temperature}
                        helperText={weatherErrors?.temperature}
                        FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: '2px', ml: 0, lineHeight: 1.2 } }}
                    />
                </Grid2>

                {/* Wind Speed */}
                <Grid2 size={{ xs: 6 }}>
                    <TextField
                        fullWidth
                        value={weatherData?.wind_speed ?? ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                onWeatherChange?.({ wind_speed: value === '' ? null : value });
                            }
                        }}
                        onBlur={() => {
                            const value = weatherData?.wind_speed;
                            if (value === null || value === undefined || value === '') {
                                onWeatherChange?.({ wind_speed: null });
                            } else {
                                const numValue = Number(value);
                                if (!isNaN(numValue)) onWeatherChange?.({ wind_speed: numValue });
                            }
                        }}
                        variant="outlined"
                        placeholder="0.0"
                        type="text"
                        inputMode="decimal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>km/u</Typography>
                                </InputAdornment>
                            ),
                        }}
                        error={!!weatherErrors?.wind_speed}
                        helperText={weatherErrors?.wind_speed}
                        FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: '2px', ml: 0, lineHeight: 1.2 } }}
                    />
                </Grid2>

                {/* Wind Direction */}
                <Grid2 size={{ xs: 6 }}>
                    <Autocomplete
                        options={WIND_DIRECTIONS}
                        getOptionLabel={(option) => option.label}
                        value={WIND_DIRECTIONS.find(d => d.value === weatherData?.wind_direction) ?? null}
                        onChange={(_, newValue) => {
                            onWeatherChange?.({ wind_direction: newValue ? newValue.value : null });
                        }}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        slotProps={{
                            listbox: { style: { maxHeight: 200 } },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Kies..."
                            />
                        )}
                    />
                </Grid2>

                {/* Weather Condition */}
                <Grid2 size={{ xs: 6 }}>
                    <TextField
                        fullWidth
                        value={weatherData?.weather_code ?? ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 40 && (value === '' || /^[a-zA-Z\s]*$/.test(value))) {
                                onWeatherChange?.({ weather_code: value || null });
                            }
                        }}
                        variant="outlined"
                        placeholder="Conditie"
                        type="text"
                        error={!!weatherErrors?.weather_code}
                        helperText={weatherErrors?.weather_code}
                        FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: '2px', ml: 0, lineHeight: 1.2 } }}
                    />
                </Grid2>
            </Grid2>
        </Box>
    );
};